import { useRef, useState, type FormEvent } from 'react';
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowUpRight as ExternalArrow,
  CalendarClock,
  FileText,
  Image as ImageIcon,
  Inbox,
  Mail,
  MessageSquare,
  Paperclip,
  Phone,
  Plus,
  StickyNote,
  Trash2,
  Upload,
  X as XIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { SentEmailDialog } from '@/components/sent-email-dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { EmptyState } from '@/components/empty-state';
import { IconButton } from '@/components/icon-button';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useTimeline, useCreateTimelineEvent, useDeleteTimelineEvent } from '@/lib/timeline';
import { useFormError } from '@/lib/form-error';
import { formatDateTime } from '@/lib/format';
import type { TimelineEventData } from '@/types/api';

const TYPE_ICON: Record<string, typeof StickyNote> = {
  note: StickyNote,
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  visit: CalendarClock,
  system: Activity,
  form: Inbox,
};

export function ClientTimeline({ clientId }: { clientId: number }) {
  const { data, isLoading } = useTimeline(clientId);
  const create = useCreateTimelineEvent(clientId);
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" /> Log event
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="No activity yet"
          description="Log calls, texts, visits, and notes — automated activity will land here too."
        />
      ) : (
        <ol className="relative space-y-3 border-l border-[var(--color-border)] pl-6">
          {data.map((e) => (
            <Entry key={e.id} event={e} clientId={clientId} />
          ))}
        </ol>
      )}

      <EventDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={async (input) => {
          await create.mutateAsync(input);
          setOpen(false);
        }}
      />
    </div>
  );
}

function Entry({ event, clientId }: { event: TimelineEventData; clientId: number }) {
  const Icon = TYPE_ICON[event.type] ?? StickyNote;
  const titleText = event.title ?? capitalize(event.type);
  const del = useDeleteTimelineEvent(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  // Only hand-logged entries are ours to remove. Everything else is a record of
  // something that happened, owned by whatever wrote it, and the merged feed
  // would rebuild it on the next read anyway. Their ids are prefixed by source,
  // so the numeric id is only meaningful for an `event:` row.
  const manualId =
    event.source === 'manual' && event.id.startsWith('event:')
      ? Number(event.id.slice('event:'.length))
      : null;

  // Emails the app sent carry their dispatch, which holds the copy that went out.
  const dispatchId =
    event.source === 'mail' && typeof event.meta.dispatch_id === 'number' ? event.meta.dispatch_id : null;
  const [viewingEmail, setViewingEmail] = useState(false);

  return (
    <li className="group/entry relative">
      <span className="absolute -left-[33px] flex size-6 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)]">
        <Icon className="size-3.5 text-[var(--color-foreground-muted)]" />
      </span>
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <SubjectLink event={event} titleText={titleText} />
            {event.direction ? (
              event.direction === 'inbound' ? (
                <ArrowDownLeft className="size-3 text-[var(--color-foreground-subtle)]" />
              ) : (
                <ArrowUpRight className="size-3 text-[var(--color-foreground-subtle)]" />
              )
            ) : null}
            {event.source === 'activitylog' ? (
              <span className="text-xs text-[var(--color-foreground-subtle)]">· system</span>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {dispatchId !== null ? (
              <button
                type="button"
                onClick={() => setViewingEmail(true)}
                className="rounded-md px-1.5 py-0.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
              >
                View email
              </button>
            ) : null}
            <span className="text-xs text-[var(--color-foreground-subtle)]">
              {formatDateTime(event.occurred_at)}
            </span>
            {manualId !== null ? (
              <IconButton
                type="button"
                aria-label="Delete entry"
                className="size-6 opacity-0 transition-opacity focus:opacity-100 group-hover/entry:opacity-100"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete this entry?',
                    body: event.attachments.length > 0
                      ? `The entry and its ${event.attachments.length} attachment${event.attachments.length === 1 ? '' : 's'} are removed for good.`
                      : 'The entry is removed for good.',
                    confirmLabel: 'Delete entry',
                    tone: 'danger',
                  });
                  if (ok) {
                    await del.mutateAsync(manualId);
                    toast.success('Entry deleted');
                  }
                }}
              >
                <Trash2 className="size-3.5" />
              </IconButton>
            ) : null}
          </div>
        </CardHeader>
        {event.body ? <CardBody className="whitespace-pre-wrap text-sm">{event.body}</CardBody> : null}
        {event.attachments.length > 0 ? (
          <CardBody className="border-t border-[var(--color-border)]">
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {event.attachments.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-xs hover:bg-[var(--color-surface-muted)]"
                >
                  {a.is_image ? (
                    <img src={a.url} alt={a.file_name} className="size-8 shrink-0 rounded object-cover" />
                  ) : (
                    <div className="flex size-8 shrink-0 items-center justify-center rounded bg-[var(--color-surface-muted)]">
                      <FileText className="size-3.5 text-[var(--color-foreground-muted)]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{a.file_name}</div>
                    <div className="text-[var(--color-foreground-muted)]">{formatBytes(a.file_size)}</div>
                  </div>
                </a>
              ))}
            </div>
          </CardBody>
        ) : null}
      </Card>
      {viewingEmail && dispatchId !== null ? (
        <SentEmailDialog dispatchId={dispatchId} onClose={() => setViewingEmail(false)} />
      ) : null}
    </li>
  );
}

function SubjectLink({ event, titleText }: { event: TimelineEventData; titleText: string }) {
  if (!event.link_url) {
    return <span className="font-medium">{titleText}</span>;
  }

  // External link (uploaded file, public approvable URL): open in a new tab and
  // mark with an arrow so it's clear we're leaving the SPA.
  if (event.link_external) {
    return (
      <a
        href={event.link_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary hover:underline"
      >
        {titleText}
        <ExternalArrow className="size-3 text-muted-foreground" />
      </a>
    );
  }

  // SPA route (project page, contacts tab).
  return (
    <Link
      to={event.link_url}
      className="font-medium text-foreground hover:text-primary hover:underline"
    >
      {titleText}
    </Link>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function EventDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    type: string;
    direction?: string;
    title?: string;
    body?: string;
    files?: File[];
  }) => Promise<void>;
}) {
  const [type, setType] = useState('note');
  const [direction, setDirection] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  const needsDirection = ['call', 'sms', 'email'].includes(type);

  function addFiles(list: FileList | File[]) {
    setFiles((curr) => [...curr, ...Array.from(list)]);
  }

  function removeFile(idx: number) {
    setFiles((curr) => curr.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const ok = await wrap(() =>
      onSubmit({
        type,
        direction: needsDirection ? direction || undefined : undefined,
        title: title || undefined,
        body: body || undefined,
        files: files.length > 0 ? files : undefined,
      }),
    );
    setPending(false);
    if (ok) {
      setType('note');
      setDirection('');
      setTitle('');
      setBody('');
      setFiles([]);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Log timeline event">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-xs">
            Type
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="note">Note</option>
              <option value="call">Call</option>
              <option value="sms">Text</option>
              <option value="email">Email</option>
              <option value="visit">Site visit</option>
            </Select>
          </label>
          {needsDirection ? (
            <label className="space-y-1 text-xs">
              Direction
              <Select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="">—</option>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </Select>
            </label>
          ) : null}
        </div>
        <Input
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          placeholder="Details…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)]">
              <Paperclip className="size-3.5" /> Attachments
              {files.length > 0 ? (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-surface-muted)] px-1.5 text-[10px] tabular-nums">
                  {files.length}
                </span>
              ) : null}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-3.5" /> Add files
            </Button>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
            }}
            className={
              files.length === 0
                ? `flex items-center justify-center rounded-md border border-dashed py-4 text-xs transition-colors ${
                    dragging
                      ? 'border-[var(--color-primary)] bg-[var(--color-surface-muted)] text-[var(--color-foreground)]'
                      : 'border-[var(--color-border)] text-[var(--color-foreground-subtle)]'
                  }`
                : `space-y-1.5 rounded-md border border-dashed p-2 transition-colors ${
                    dragging ? 'border-[var(--color-primary)] bg-[var(--color-surface-muted)]' : 'border-transparent'
                  }`
            }
          >
            {files.length === 0 ? (
              <span>Drop receipts, screenshots, voicemails… or click Add files</span>
            ) : (
              files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs"
                >
                  {f.type.startsWith('image/') ? (
                    <ImageIcon className="size-3 shrink-0 text-[var(--color-foreground-muted)]" />
                  ) : (
                    <Paperclip className="size-3 shrink-0 text-[var(--color-foreground-muted)]" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="shrink-0 text-[var(--color-foreground-muted)]">
                    {formatBytes(f.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="rounded p-0.5 text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)]"
                    aria-label={`Remove ${f.name}`}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Log event'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
